import { useState, useRef, useEffect } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const monthNamesShort = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

export const monthNamesFull = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export default function MonthPicker({ month, year, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(year);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setPickerYear(year); }, [year]);

  function handlePrev() {
    let m = month - 1, y = year;
    if (m < 1) { m = 12; y--; }
    onChange(m, y);
  }

  function handleNext() {
    let m = month + 1, y = year;
    if (m > 12) { m = 1; y++; }
    onChange(m, y);
  }

  return (
    <div className="flex items-center gap-1 relative" ref={ref}>
      <button
        onClick={handlePrev}
        disabled={disabled}
        className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition text-slate-400 border border-transparent hover:border-slate-200 disabled:opacity-30 disabled:pointer-events-none"
      >
        <CaretLeft size={16} />
      </button>

      <button
        onClick={() => !disabled && setOpen(!open)}
        className="text-base sm:text-lg font-semibold text-slate-800 min-w-0 sm:min-w-[160px] text-center hover:bg-white hover:shadow-sm rounded-lg px-2 sm:px-3 py-1.5 transition border border-transparent hover:border-slate-200"
      >
        <span className="sm:hidden">{monthNamesShort[month - 1]} {year}</span>
        <span className="hidden sm:inline">{monthNamesFull[month - 1]} {year}</span>
      </button>

      <button
        onClick={handleNext}
        disabled={disabled}
        className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition text-slate-400 border border-transparent hover:border-slate-200 disabled:opacity-30 disabled:pointer-events-none"
      >
        <CaretRight size={16} />
      </button>

      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-lg border border-slate-200/60 p-4 z-50 w-64">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setPickerYear(pickerYear - 1)} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 transition">
              <CaretLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-slate-800">{pickerYear}</span>
            <button onClick={() => setPickerYear(pickerYear + 1)} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 transition">
              <CaretRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {monthNamesShort.map((name, i) => {
              const isActive = (i + 1) === month && pickerYear === year;
              return (
                <button
                  key={i}
                  onClick={() => { onChange(i + 1, pickerYear); setOpen(false); }}
                  className={`py-2 rounded-lg text-xs font-medium transition ${
                    isActive ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
